import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase/config";
import { setUser, clearUser } from "./store/slices/authSlice";
import { doc, getDoc } from "firebase/firestore";
import DashboardLayout from "./components/layout/DashboardLayout";
import { PageTitleProvider } from "./context/PageTitleContext";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Services from "./pages/Services";
import Packages from "./pages/Packages";
import Billing from "./pages/Billing";
import PurchaseHistory from "./pages/PurchaseHistory";
import Support from "./pages/Support";
import Settings from "./pages/Settings";
import DevTools from "./pages/DevTools";
import Profile from "./pages/Profile";
import SalesTracking from "./pages/SalesTracking";
import UpdatedPricing from './pages/UpdatedPricing';
import GuestCheckout from './pages/GuestCheckout';
import PaymentSuccess from './pages/PaymentSuccess';
import "./App.scss";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, status } = useSelector((state) => state.auth);
  
  // If still checking authentication status, show loading
  if (status === 'loading') {
    return <div className="loading-screen">Loading...</div>;
  }
  
  // If not authenticated, redirect to landing page
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // If authenticated, render the protected route
  return children;
};

// Admin route component - only allow access for admin users
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, status } = useSelector((state) => state.auth);
  
  // If still checking authentication status, show loading
  if (status === 'loading') {
    return <div className="loading-screen">Loading...</div>;
  }
  
  // If not authenticated or not admin, redirect to dashboard
  if (!isAuthenticated || user?.userRole !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  // If authenticated and admin, render the route
  return children;
};

// For development tools - only allow access in development mode and for admin users
const DevToolsRoute = ({ children }) => {
  const { isAuthenticated, status } = useSelector((state) => state.auth);
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // If still checking authentication status, show loading
  if (status === 'loading') {
    return <div className="loading-screen">Loading...</div>;
  }
  
  // In development, only require authentication
  // In production, disable this route entirely
  if (!isDevelopment || !isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // If conditions are met, render the DevTools
  return children;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        // Get additional user data from Firestore
        try {
          const userDoc = await getDoc(
            doc(db, "fredoRegisteredUsers", user.uid)
          );

          if (userDoc.exists()) {
            // User data found in Firestore
            dispatch(
              setUser({
                uid: user.uid,
                email: user.email,
                ...userDoc.data(),
              })
            );
          } else {
            // User exists in auth but not in Firestore
            dispatch(
              setUser({
                uid: user.uid,
                email: user.email,
              })
            );
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email,
            })
          );
        }
      } else {
        // User is signed out
        dispatch(clearUser());
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [dispatch]);

  return (
    <PageTitleProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={!isAuthenticated ? <Landing /> : <Navigate to="/dashboard" replace />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Dashboard />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/services" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Services />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/packages" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Packages />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/billing" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Billing />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/purchases" element={
            <ProtectedRoute>
              <DashboardLayout>
                <PurchaseHistory />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/support" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Support />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Settings />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Admin only routes */}
          <Route path="/sales-tracking" element={
            <AdminRoute>
              <DashboardLayout>
                <SalesTracking />
              </DashboardLayout>
            </AdminRoute>
          } />
          
          {/* DevTools route - restricted to dev environment and admin users */}
          <Route path="/dev-tools" element={
            <DevToolsRoute>
              <DashboardLayout>
                <DevTools />
              </DashboardLayout>
            </DevToolsRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <DashboardLayout>
                <Profile />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/updated-pricing-for-website-services" element={<UpdatedPricing />} />
          <Route path="/guest-checkout" element={<GuestCheckout />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Redirect any unknown routes to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </PageTitleProvider>
  );
}

export default App;
